'use client'

import { clsx } from 'clsx'
import React, { useCallback, useEffect, useMemo } from 'react'

import Button from 'lib/components/Button/Button'
import DocumentReminder from 'lib/components/DocumentReminder/DocumentReminder'
import Header from 'lib/components/Header/Header'
import { LOGIN_MODAL_ID } from 'lib/components/LoginModal/LoginModal'
import { useModal } from 'lib/components/Modal/Modal'
import OrderTracking from 'lib/components/OrderTracking/OrderTracking'
import OrderTrackingMap from 'lib/components/OrderTracking/OrderTrackingMap'
import SmsSignupModal, { SMS_SIGNUP_MODAL_ID } from 'lib/components/SmsSignupModal/SmsSignupModal'
import { IconNames } from 'lib/settings/icons'
import { RouteLocation, RouteLocationStatus } from 'lib/types/maps'

import { useSearchOrders } from '@/hooks/useSearchUserOrders'
import { brand } from '@/utils/brand'
import { getBaseUrl } from '@/utils/urlHelpers'

import CreateAnAccountSection from '../order-confirmation/CreateAnAccountSection'

type Props = {
  orderId: string | undefined
}

const smsButton = {
  icon: 'phone' as IconNames,
  text: 'Sign up for SMS updates',
}

const loginButton = {
  icon: 'details' as IconNames,
  text: 'Login to view details',
}

const getLegAddress = (point: Address): string => {
  return `${point.city}, ${point.state} ${point.zip} ${point.country}`
}

const allowGuestPrintLabel =
  brand.key === 'shipsticks' || brand.key === 'shipskis' || brand.key === 'shipplay'

const OrderTrackingClient: React.FC<Props> = ({ orderId }) => {
  const [orderLegs, setOrderLegs] = React.useState<OrderLeg[]>([])
  const [activeShipmentId, setActiveShipmentId] = React.useState<string | undefined>(undefined)

  const {
    searchError,
    orders,
    user,
    searchOrdersLoading,
    isLoggedIn,
    handleOrderSearch,
    isSmsSubscribed,
    updateSMSSubscription,
    isCreatingSmsTracking,
    createdSmsTracking,
    smsOptInError,
  } = useSearchOrders()
  const openDialog = useModal((state) => state.openDialog)

  useEffect(() => {
    // Trigger search with the orderId from params. isLoggedIn is a dependency
    // on purpose: the guest session bootstrap sets the auth token AFTER mount,
    // and the hook resets its query on token changes — without re-issuing the
    // search here, the page strands on an empty (disabled) query forever.
    if (orderId) {
      handleOrderSearch({ searchParam: orderId })
    }
  }, [orderId, isLoggedIn, handleOrderSearch])

  useEffect(() => {
    if (orders && orders.length > 0) {
      const order = orders[0]
      // Set the first order's shipping address as the current address
      const legs = order.legs || []
      setOrderLegs(legs)
      if (orderId?.[0].toUpperCase() !== 'O') {
        setActiveShipmentId(orderId)
      }
    }
  }, [orderId, orders])

  const handleSmsButtonClick = useCallback(() => {
    if (isLoggedIn) {
      openDialog(SMS_SIGNUP_MODAL_ID)
    }
  }, [isLoggedIn, openDialog])

  const handleSmsSignupSubmit = useCallback(
    (data: Record<string, unknown>) => {
      const { phone, terms } = data
      if (!phone || !terms) return
      updateSMSSubscription?.(phone as string, terms as boolean)
    },
    [updateSMSSubscription],
  )

  const handleLoginButtonClick = useCallback(() => {
    if (!isLoggedIn) {
      openDialog(LOGIN_MODAL_ID)
    }
  }, [isLoggedIn, openDialog])

  const routeLocations = useMemo(() => {
    if (!orderLegs || orderLegs.length === 0) return []

    const locations: RouteLocation[] = []
    const leg = orderLegs[0]
    if (leg) {
      const status = (leg?.travelers?.[0]?.shipments?.[0]?.state ||
        'pending') as RouteLocationStatus

      locations.push({
        id: `${leg.leg_id} - origin`,
        name: leg.leg_name,
        address: getLegAddress(leg.origin),
        type: 'origin',
        status: status,
      })

      locations.push({
        id: `${leg.leg_id} - destination`,
        name: leg.leg_name,
        address: getLegAddress(leg.destination),
        type: 'destination',
        status: status,
      })
    }
    return locations
  }, [orderLegs])

  // Gate the travel-documents CTA on the backend flag, not on the route
  // looking international. The flag follows the per-brand country toggles
  // (admin "Travel Documents Flow O/D") on the live shipment route — an
  // international order with both toggles off must NOT ask for documents
  // nobody will review (GSE-10946).
  const requiresTravelDocuments = orders?.[0]?.requires_travel_documents === true

  return (
    <div className="w-full items-center">
      <div className="pt-24 sm:px-0 sm:pt-6 md:px-[40px] md:pt-6 lg:px-[64px] lg:pt-20">
        <header
          className={clsx(
            'flex gap-y-4 lg:items-center lg:justify-between',
            'mb-6 md-max:flex-col md-max:px-6',
          )}
        >
          <Header
            as="h2"
            className="type-headline-3-mobile md:type-headline-3-tablet lg:type-headline-3-desktop"
          >
            Order Tracking
          </Header>
          <div>
            {isLoggedIn && !isSmsSubscribed && (
              <Button
                onClick={handleSmsButtonClick}
                icon={smsButton.icon as IconNames}
                type="button"
              >
                {smsButton.text}
              </Button>
            )}
            {!isLoggedIn && (
              <Button
                onClick={handleLoginButtonClick}
                icon={loginButton.icon as IconNames}
                type="button"
              >
                {loginButton.text}
              </Button>
            )}
          </div>
        </header>
        <div className="mb-6 flex flex-col gap-6 md:flex-row">
          <div className="flex-1">
            <OrderTrackingMap routeLocations={routeLocations} showRoute={true} />
          </div>
          {requiresTravelDocuments && (
            <div className="flex flex-1 items-center justify-center">
              <DocumentReminder
                layout="large"
                button={{
                  onClick: () => {
                    const baseUrl = getBaseUrl()
                    if (orderId) {
                      window.open(`${baseUrl}/travel_documents/${orderId}`, '_blank')
                    }
                  },
                }}
              />
            </div>
          )}
        </div>

        {searchOrdersLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-600">Loading orders...</div>
          </div>
        )}

        {!searchOrdersLoading && orders && orders.length > 0 && (
          <div className="h-full md:min-h-[300px]">
            <OrderTracking
              key={orders[0].id}
              order={orders[0]}
              isLoggedIn={isLoggedIn}
              expandShipments={true}
              activeShipmentId={activeShipmentId}
              phoneNumber={orders[0].user?.phone_number}
              allowGuestPrintLabel={allowGuestPrintLabel}
            />
          </div>
        )}

        <div className="my-4">
          <CreateAnAccountSection user={user} />
        </div>

        {!searchOrdersLoading && (searchError || orders?.length == 0) && (
          <div className="py-8 text-center text-gray-600">
            {searchError && <p>{searchError}</p>}
            {/* Not loading + no error + no orders is a terminal state — an
                actual message, never a spinner that can't resolve. */}
            {!searchError && orders?.length === 0 && (
              <p>We couldn&apos;t find that order. Please check the link and try again.</p>
            )}
          </div>
        )}
      </div>
      <SmsSignupModal
        onSubmit={handleSmsSignupSubmit}
        isUpdatingSmsOptin={isCreatingSmsTracking}
        smsOptinSuccess={createdSmsTracking}
        smsOptinError={smsOptInError}
      />
    </div>
  )
}

export default OrderTrackingClient
